import { Controller } from "@hotwired/stimulus"

// Always show overlay preview first (WYSIWYG sharing).
// Mobile gets an extra "Share" button that triggers navigator.share with the file.
export default class extends Controller {
  static targets = ["overlay", "image", "loading", "copyBtn", "copyLabel", "nativeShareBtn"]
  static values = { url: String, filename: String }

  connect() {
    this._blob = null
    this._objectUrl = null
    this._preloaded = false
    this._boundKeydown = this._onKeydown.bind(this)
  }

  disconnect() {
    this._cleanup()
  }

  // Preload image on hover
  preload() {
    if (this._preloaded || this._blob) return
    this._preloaded = true
    fetch(this.urlValue)
      .then(r => r.ok ? r.blob() : null)
      .then(blob => { if (blob) this._blob = blob })
      .catch(() => {})
  }

  // Always open overlay — user sees card first, then decides action
  open() {
    this._showOverlay()
  }

  // Native share (mobile) — only after user has previewed
  async nativeShare() {
    try {
      const blob = this._blob || await this._fetchBlob()
      if (!blob) return
      const file = new File([blob], this.filenameValue || "share.png", { type: "image/png" })
      await navigator.share({ files: [file] })
    } catch (e) {
      if (e.name !== "AbortError") {
        // Fallback: download instead
        this.download()
      }
    }
  }

  async copy() {
    try {
      const blob = this._blob || await this._fetchBlob()
      if (!blob) return
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob })
      ])
      this._showCopied()
    } catch {
      this._disableCopy()
    }
  }

  download() {
    const url = this._objectUrl || this.urlValue
    const a = document.createElement("a")
    a.href = url
    a.download = this.filenameValue || "share.png"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  close() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.remove("is-open")
    document.removeEventListener("keydown", this._boundKeydown)
  }

  clickOverlay(event) {
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  // Private

  _canNativeShare() {
    return typeof navigator.share === "function" &&
      typeof File !== "undefined" &&
      navigator.canShare?.({ files: [new File([""], "t.png", { type: "image/png" })] })
  }

  async _fetchBlob() {
    try {
      const response = await fetch(this.urlValue)
      if (!response.ok) return null
      const blob = await response.blob()
      this._blob = blob
      return blob
    } catch {
      return null
    }
  }

  _showOverlay() {
    if (!this.hasOverlayTarget) return

    this.overlayTarget.classList.add("is-open")
    document.addEventListener("keydown", this._boundKeydown)

    // Show/hide native share button based on capability
    if (this.hasNativeShareBtnTarget) {
      this.nativeShareBtnTarget.hidden = !this._canNativeShare()
    }

    // Load image
    if (this.hasImageTarget) {
      if (this._blob) {
        this._setImage(this._blob)
      } else {
        if (this.hasLoadingTarget) this.loadingTarget.hidden = false
        this.imageTarget.hidden = true
        this._fetchBlob().then(blob => {
          if (blob) this._setImage(blob)
        })
      }
    }

    // Check clipboard support
    if (this.hasCopyBtnTarget && !navigator.clipboard?.write) {
      this._disableCopy()
    }
  }

  _setImage(blob) {
    if (this._objectUrl) URL.revokeObjectURL(this._objectUrl)
    this._objectUrl = URL.createObjectURL(blob)
    this.imageTarget.src = this._objectUrl
    this.imageTarget.hidden = false
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true
  }

  _showCopied() {
    if (!this.hasCopyLabelTarget) return
    const original = this.copyLabelTarget.textContent
    this.copyLabelTarget.textContent = this.copyLabelTarget.dataset.copiedText || "Copied!"
    this.copyBtnTarget.classList.add("is-copied")
    setTimeout(() => {
      this.copyLabelTarget.textContent = original
      this.copyBtnTarget.classList.remove("is-copied")
    }, 2000)
  }

  _disableCopy() {
    if (!this.hasCopyBtnTarget) return
    this.copyBtnTarget.disabled = true
    this.copyBtnTarget.style.opacity = "0.4"
  }

  _onKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  _cleanup() {
    if (this._objectUrl) {
      URL.revokeObjectURL(this._objectUrl)
      this._objectUrl = null
    }
    document.removeEventListener("keydown", this._boundKeydown)
  }
}
